/**
 * RewardsTab — per-event "Generic Event Reward Email" configuration.
 * Links offers to the event (event_offers) and toggles auto-issue on registration
 * and/or on attendance. Distinct from the gun-show Conversion Engine reward.
 */
import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import Button from "../../common/Button";
import LoadingSpinner from "../../common/LoadingSpinner";
import { useToast } from "../../ui/Toast";
import { useAuth } from "../../../contexts/AuthContext";
import {
  getEventOffers,
  getAvailableOffers,
  attachOfferToEvent,
  updateEventOffer,
  removeOfferFromEvent,
  sendEventRewardReminder,
} from "../../../services/eventOffers.service";
import { listSendGridTemplates } from "../../../services/templates.service";
import EnableTicketingPanel from "../EnableTicketingPanel";
import type { Event } from "../../../types/event.types";
import type { EventOffer } from "../../../types/program.types";

interface RewardsTabProps {
  event: Event;
}

const RewardsTab: React.FC<RewardsTabProps> = ({ event }) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [remindBusyId, setRemindBusyId] = useState<string | null>(null);
  // Reminder template chosen per offer (may differ from the on-register
  // template). Keyed by event_offer id; falls back to the offer's template.
  const [reminderTemplateByOffer, setReminderTemplateByOffer] = useState<
    Record<string, string>
  >({});

  // Reminders blast real recipients, so restrict to admin/superadmin only.
  const role = (user?.role || "").toLowerCase().replace(/_/g, "");
  const canSendReminder = role === "admin" || role === "superadmin";

  // A conversion-engine event already rewards via the booth ladder; block the
  // generic attendance reward to avoid a double reward (design v3, blocker #3).
  const isConversionEngineEvent = Boolean(
    (event as unknown as { conversion_engine_id?: string })
      .conversion_engine_id,
  );

  const { data: eventOffers, isLoading } = useQuery({
    queryKey: ["eventRewardOffers", event.id],
    queryFn: () => getEventOffers(event.id),
  });
  const { data: availableOffers } = useQuery({
    queryKey: ["availableOffers"],
    queryFn: () => getAvailableOffers(),
  });
  // SendGrid templates for the reward-email selector. Loaded lazily-ish via the
  // shared list endpoint; failures are non-fatal (selector just shows the raw id).
  const { data: sendGridTemplates } = useQuery({
    queryKey: ["sendGridTemplates"],
    queryFn: () => listSendGridTemplates(),
    staleTime: 5 * 60 * 1000,
  });

  const linkedOfferIds = new Set((eventOffers || []).map((eo) => eo.offer_id));
  const attachable = (availableOffers || []).filter(
    (o) => !linkedOfferIds.has(o.id),
  );

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["eventRewardOffers", event.id],
    });

  const handleAttach = async () => {
    if (!selectedOfferId) {
      return;
    }
    setAdding(true);
    try {
      await attachOfferToEvent(
        { event_id: event.id, offer_id: selectedOfferId },
        user?.id,
      );
      setSelectedOfferId("");
      showToast("success", "Offer linked to event.");
      invalidate();
    } catch (err) {
      console.error("Attach offer error:", err);
      showToast("error", "Failed to link offer");
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (
    eo: EventOffer,
    field: "auto_issue_on_register" | "auto_issue_on_attendance",
    value: boolean,
  ) => {
    setBusyId(eo.id);
    try {
      await updateEventOffer(eo.id, { [field]: value });
      invalidate();
    } catch (err) {
      console.error("Toggle reward error:", err);
      showToast("error", "Failed to update reward setting");
    } finally {
      setBusyId(null);
    }
  };

  // Phase B: turning ticket mode ON atomically clears the standalone auto-issue flags
  // (DB CHECK enforces mutual exclusion — a ticket-mode offer can't also auto-issue a
  // standalone voucher for the same person). Turning it OFF leaves the auto flags cleared.
  const handleToggleTicket = async (eo: EventOffer, value: boolean) => {
    setBusyId(eo.id);
    try {
      await updateEventOffer(
        eo.id,
        value
          ? {
              issue_as_ticket: true,
              auto_issue_on_register: false,
              auto_issue_on_attendance: false,
            }
          : { issue_as_ticket: false },
      );
      invalidate();
    } catch (err) {
      console.error("Toggle ticket mode error:", err);
      showToast("error", "Failed to update ticket setting");
    } finally {
      setBusyId(null);
    }
  };

  const handleSetTemplate = async (eo: EventOffer, templateId: string) => {
    setBusyId(eo.id);
    try {
      // Event-specific: stored on event_offers (NOT the global offer), so it
      // doesn't change other events/CTAs using the same offer.
      await updateEventOffer(eo.id, {
        reward_email_template_id: templateId || null,
      });
      showToast(
        "success",
        templateId
          ? "Reward email template set."
          : "Reward email template cleared.",
      );
      invalidate();
    } catch (err) {
      console.error("Set reward template error:", err);
      showToast("error", "Failed to set email template");
    } finally {
      setBusyId(null);
    }
  };

  const handleSendReminder = async (eo: EventOffer) => {
    if (
      !window.confirm(
        "Re-send the reward voucher email (same code + QR) to everyone who already received one for this event? Suppressed/unsubscribed contacts are skipped.",
      )
    ) {
      return;
    }
    setRemindBusyId(eo.id);
    try {
      const templateId =
        reminderTemplateByOffer[eo.id] ??
        eo.reward_email_template_id ??
        undefined;
      const res = await sendEventRewardReminder({
        eventId: event.id,
        eventOfferId: eo.id,
        templateId: templateId || undefined,
      });
      showToast(
        "success",
        `Reminder sent: ${res.sent} sent, ${res.skipped} skipped, ${res.failed} failed.`,
      );
    } catch (err) {
      console.error("Send reminder error:", err);
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to send reminder",
      );
    } finally {
      setRemindBusyId(null);
    }
  };

  const handleRemove = async (eo: EventOffer) => {
    setBusyId(eo.id);
    try {
      await removeOfferFromEvent(eo.id);
      showToast("success", "Offer unlinked.");
      invalidate();
    } catch (err) {
      console.error("Remove offer error:", err);
      showToast("error", "Failed to unlink offer");
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Event in a Box — one-step ticket + check-in-reward setup. Sits above the
          granular per-offer reward manager below. */}
      <EnableTicketingPanel event={event} />

      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Event Reward Offers
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Automatically email an offer voucher to people for this event — when
          they register and/or when they check in. One unique code per person,
          redeemable at the host retailer. This is a generic event reward,
          separate from the gun-show Conversion Engine.
        </p>
      </div>

      {(eventOffers || []).length === 0 && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-500 text-center">
          No reward offers linked yet. Link an offer below to start issuing
          rewards.
        </div>
      )}

      {(eventOffers || []).map((eo) => {
        const offer = eo.offer;
        const hasTemplate = Boolean(eo.reward_email_template_id);
        const offerActive = offer?.is_active !== false;
        return (
          <div
            key={eo.id}
            className="bg-white border border-gray-200 rounded-lg p-4"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">
                    {offer?.display_name || offer?.name || "Offer"}
                  </span>
                  {offer?.offer_code && (
                    <span className="text-xs text-gray-400">
                      {offer.offer_code}
                    </span>
                  )}
                  {!offerActive && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">
                      Offer inactive
                    </span>
                  )}
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      hasTemplate
                        ? "bg-green-100 text-green-800"
                        : "bg-amber-100 text-amber-900"
                    }`}
                  >
                    {hasTemplate
                      ? "Email template set"
                      : "No email template — voucher issued, not emailed"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => handleRemove(eo)}
                disabled={busyId === eo.id}
                className="text-xs font-medium text-red-600 hover:text-red-800 disabled:text-gray-400 inline-flex items-center gap-1 shrink-0"
              >
                <TrashIcon className="w-4 h-4" />
                Unlink
              </button>
            </div>

            <div className="mt-3 space-y-2">
              {/* Phase B — issue this offer as an Event Ticket (admission + reward) instead
                  of a standalone voucher. The offer itself is the ticket's reward: a $0
                  placeholder → assign value later; a real offer → redeemable immediately.
                  Mutually exclusive with the auto-issue toggles below. */}
              <label
                className={`flex items-start gap-2 text-sm rounded-md px-2 py-2 ${
                  eo.issue_as_ticket
                    ? "bg-indigo-50 text-indigo-900"
                    : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={Boolean(eo.issue_as_ticket)}
                  disabled={busyId === eo.id}
                  onChange={(e) => handleToggleTicket(eo, e.target.checked)}
                />
                <span>
                  Issue as an <strong>Event Ticket</strong> (admission + reward)
                  <span className="block text-xs text-gray-500 mt-0.5">
                    One TK- code per person = admission at check-in and the
                    reward at <code>/redeem</code>.{" "}
                    {offer?.is_placeholder
                      ? "This is a $0 placeholder — assign the reward value later."
                      : "The reward is this offer's value, redeemable immediately."}{" "}
                    Issued once per person via any channel (registration,
                    check-in, flight, booth).
                  </span>
                </span>
              </label>

              <label
                className={`flex items-center gap-2 text-sm ${
                  eo.issue_as_ticket ? "text-gray-400" : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  checked={Boolean(eo.auto_issue_on_register)}
                  disabled={busyId === eo.id || Boolean(eo.issue_as_ticket)}
                  onChange={(e) =>
                    handleToggle(eo, "auto_issue_on_register", e.target.checked)
                  }
                />
                Email this offer to <strong>registrants</strong> automatically
                {eo.issue_as_ticket && (
                  <span className="text-xs text-indigo-700">
                    — replaced by ticket issuance
                  </span>
                )}
              </label>
              <label
                className={`flex items-center gap-2 text-sm ${
                  isConversionEngineEvent || eo.issue_as_ticket
                    ? "text-gray-400"
                    : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  checked={Boolean(eo.auto_issue_on_attendance)}
                  disabled={
                    busyId === eo.id ||
                    isConversionEngineEvent ||
                    Boolean(eo.issue_as_ticket)
                  }
                  onChange={(e) =>
                    handleToggle(
                      eo,
                      "auto_issue_on_attendance",
                      e.target.checked,
                    )
                  }
                />
                Email this offer to <strong>attendees</strong> when they check
                in
                {isConversionEngineEvent ? (
                  <span className="text-xs text-amber-700">
                    — disabled: this event already uses Conversion Engine
                    rewards
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">
                    — issued shortly after each check-in (needs a reward email
                    template below)
                  </span>
                )}
              </label>
              {/* Reward email template — the SendGrid dynamic template used to
                  deliver this offer's voucher (code + QR). Stored on the offer,
                  so it applies wherever this offer is used. The template should
                  map the voucher merge fields ({{voucher_code}}, {{qr_code_url}}). */}
              <div className="pt-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Reward email template
                </label>
                <select
                  value={eo.reward_email_template_id || ""}
                  disabled={busyId === eo.id}
                  onChange={(e) => handleSetTemplate(eo, e.target.value)}
                  className="w-full max-w-md px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">
                    No email — issue voucher only (no email sent)
                  </option>
                  {(sendGridTemplates || []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.id})
                    </option>
                  ))}
                  {/* Preserve a currently-set id that isn't in the fetched list
                      (e.g. list failed to load) so we don't silently blank it. */}
                  {eo.reward_email_template_id &&
                    !(sendGridTemplates || []).some(
                      (t) => t.id === eo.reward_email_template_id,
                    ) && (
                      <option value={eo.reward_email_template_id}>
                        {eo.reward_email_template_id}
                      </option>
                    )}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Map <code>{"{{voucher_code}}"}</code> and{" "}
                  <code>{"{{qr_code_url}}"}</code> in the template to show the
                  code and QR.
                </p>
              </div>

              {(eo.auto_issue_on_register || eo.auto_issue_on_attendance) &&
                !hasTemplate && (
                  <p className="text-xs text-amber-700">
                    ⚠ No SendGrid template is set on this offer, so a voucher
                    will be issued but no email is sent. Set the offer's email
                    template above to deliver it.
                  </p>
                )}

              {/* Reminder — re-send the SAME voucher code + QR to everyone who
                  already received one for this event (e.g. a few days before).
                  Re-send only; never mints new vouchers. Suppressed contacts are
                  skipped server-side. Admin/superadmin only. The reminder email
                  can use a DIFFERENT template than the on-register one. */}
              {canSendReminder &&
                (() => {
                  const reminderTemplate =
                    reminderTemplateByOffer[eo.id] ??
                    (eo.reward_email_template_id || "");
                  return (
                    <div className="pt-2 border-t border-gray-100 mt-2 space-y-1.5">
                      <label className="block text-xs font-medium text-gray-700">
                        Reminder email template
                      </label>
                      <select
                        value={reminderTemplate}
                        disabled={remindBusyId === eo.id}
                        onChange={(e) =>
                          setReminderTemplateByOffer((prev) => ({
                            ...prev,
                            [eo.id]: e.target.value,
                          }))
                        }
                        className="w-full max-w-md px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select a template…</option>
                        {(sendGridTemplates || []).map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name} ({t.id})
                          </option>
                        ))}
                        {reminderTemplate &&
                          !(sendGridTemplates || []).some(
                            (t) => t.id === reminderTemplate,
                          ) && (
                            <option value={reminderTemplate}>
                              {reminderTemplate}
                            </option>
                          )}
                      </select>
                      <button
                        type="button"
                        onClick={() => handleSendReminder(eo)}
                        disabled={remindBusyId === eo.id || !reminderTemplate}
                        className="block text-sm font-medium text-primary-600 hover:text-primary-800 disabled:text-gray-400"
                        title={
                          reminderTemplate
                            ? "Re-send the existing voucher email to all registrants who got one"
                            : "Pick a reminder template first"
                        }
                      >
                        {remindBusyId === eo.id
                          ? "Sending reminder…"
                          : "✉ Send voucher reminder to registrants"}
                      </button>
                      <p className="text-xs text-gray-500">
                        Re-sends the same code + QR each registrant already
                        received (great a few days before the event), using the
                        template above. No new vouchers are created;
                        suppressed/unsubscribed contacts are skipped.
                      </p>
                    </div>
                  );
                })()}
            </div>
          </div>
        );
      })}

      {/* Link a new offer */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Link an offer
        </h4>
        <div className="flex gap-2">
          <select
            value={selectedOfferId}
            onChange={(e) => setSelectedOfferId(e.target.value)}
            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select an offer…</option>
            {attachable.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
                {o.offer_code ? ` (${o.offer_code})` : ""}
              </option>
            ))}
          </select>
          <Button
            variant="secondary"
            size="sm"
            disabled={!selectedOfferId || adding}
            onClick={handleAttach}
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Link
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RewardsTab;
